import { atom, useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import isEqual from 'lodash.isequal';
import { CheckOutDeliveryShippingDetailsDialogHandleConfirm } from 'components/CheckOutDeliveryShippingDetailsDialog/CheckOutDeliveryShippingDetailsDialog';
import { ShipperDetails } from 'api/models/Deliveries';

export type VisibleCheckOutDialog =
  | {
      type: 'SELECT_SETS_OR_IMPLANTS_DIALOG';
    }
  | {
      type: 'SELECT_SURGERY_SET_DIALOG';
    }
  | {
      type: 'SELECT_CONTENT_TO_CHECK_IN';
    }
  | {
      type: 'INSPECT';
    }
  | {
      type: 'SKIP_INSPECTION';
    }
  | {
      type: 'SKIP_INSPECTION_SELECT_SETS';
    }
  | {
      type: 'SKIP_INSPECTION_REASON';
      setKitInstanceIds?: string[];
    }
  | {
      type: 'SKIP_INSPECTION_SUCCESS';
      variant: 'DELIVERY' | 'SETS';
    }
  | {
      type: 'SELECT_SET';
    }
  | {
      type: 'SELECT_TRAY';
      setKitInstanceId: string;
    }
  | {
      type: 'TRAY_USED';
      setKitInstanceId: string;
      trayId: string;
    }
  | {
      type: 'TRAY_NOT_USED_CONFIRM';
      setKitInstanceId: string;
      trayId: string;
    }
  | {
      type: 'NOTIFY_SUPPLIER_CONFIRM';
      exitFlow?: boolean;
      setKitInstanceId?: string; // forwarded prop for the SHIPMENT_DETAILS_DIALOG
    }
  | {
      type: 'SURGERY_NOTIFY_SUPPLIER_CONFIRM';
    }
  | {
      type: 'NOTIFY_SUPPLIER_SUCCESS_DIALOG';
      exitFlow?: boolean;
    }
  | {
      type: 'SHIPMENT_DETAILS_DIALOG';
      isSupplierNotifyFlow?: boolean;
      exitFlow?: boolean;
      setKitInstanceId?: string; // if supplied, we have just checked out a set and need to notify the supplier
      handleConfirm?: CheckOutDeliveryShippingDetailsDialogHandleConfirm;
    }
  | {
      type: 'CHECK_OUT_SUCCESS_DIALOG';
      shipperDetails?: ShipperDetails;
    }
  | {
      type: 'CHECK_OUT_WHAT_NEXT';
      haveSetsForCheckOut?: boolean;
    }
  | {
      type: 'SURGERY_CHECK_OUT_SUCCESS_DIALOG';
      shipperDetails?: ShipperDetails;
    }
  | {
      type: 'SAVING_PLEASE_WAIT_DIALOG';
      reason?: 'saving';
    }
  | {
      type: 'IMPLANTS_AND_INSTRUMENTS_DIALOG';
    }
  | {
      type: 'SET_ALREADY_TURNED_AROUND_DIALOG';
      setKitInstanceId: string;
    }
  | {
      type: 'TURNAROUND_DESTINATION_SUCCESS_DIALOG';
      setKitInstanceId: string; // Include any additional properties needed for this dialog
    }
  | {
      type: 'SKIP_INSPECTION_SUCCESS';
      variant: 'DELIVERY' | 'SETS'; // Add the variant property here
    }
  | {
      type: 'INSPECTION_WARNING_DIALOG';
    }
  | {
      type: 'PAGE_URI'; // no dialog, a page URL to navigate to
      uri: string;
    };

// visible Check Out Dialogs stack for history navigation
const visibleCheckOutDialogsAtom = atom<VisibleCheckOutDialog[]>([]);

export const useVisibleCheckOutDialog = () => {
  const [visibleCheckOutDialogs, setVisibleCheckOutDialogs] = useAtom(visibleCheckOutDialogsAtom);

  const navigate = useNavigate();

  const setVisibleCheckOutDialog = useCallback(
    (dialog: VisibleCheckOutDialog | null) => {
      // if null passed then reset the stack
      if (!dialog) {
        setVisibleCheckOutDialogs([]);
        return;
      }

      setVisibleCheckOutDialogs(prevDialogs => {
        // prevent duplicate adds via re-renders
        if (isEqual(prevDialogs[0], dialog)) {
          return prevDialogs;
        }
        return [dialog, ...prevDialogs];
      });
    },
    [setVisibleCheckOutDialogs],
  );

  const prevVisibleCheckOutDialog = useCallback(() => {
    setVisibleCheckOutDialogs(prevDialogs => {
      return prevDialogs.slice(1);
    });
  }, [setVisibleCheckOutDialogs]);

  const visibleCheckOutDialog = visibleCheckOutDialogs[0] || null;

  // handle PAGE_URI type to navigate
  useEffect(() => {
    if (visibleCheckOutDialog?.type != 'PAGE_URI') return;

    navigate(visibleCheckOutDialog.uri);
  }, [visibleCheckOutDialog, navigate]);

  return {
    visibleCheckOutDialog,
    setVisibleCheckOutDialog,
    prevVisibleCheckOutDialog,
    haveCheckOutDialogHistory: visibleCheckOutDialogs.length > 1,
  };
};
